import React, { useState, useEffect, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Shield, 
  Activity, 
  RefreshCw, 
  Plus, 
  Search, 
  Filter, 
  LayoutGrid, 
  List, 
  MoreVertical, 
  Mail,
  ChevronDown,
  ExternalLink,
  Home, 
  Briefcase,
  Settings,
  LogOut,
  Menu,
  X,
  UserPlus,
  Trash,
  CheckCircle2,
  Clock,
  Sparkles,
  Lock,
  UserCircle,
  Wrench,
  Sun,
  Moon,
  Trash2,
  Save,
  Settings2,
  Eye,
  MapPin,
  Info,
  GripVertical
} from 'lucide-react';

/**
 * PAINEL ADMIN - VERSÃO COMPACTA PRO
 * Atualização: Adicionadas animações premium de hover nos cartões de utilizadores.
 * Redução de escala em todos os componentes para maior densidade.
 */

const App = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('Utilizadores');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInviteMode, setIsInviteMode] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [usersNotice, setUsersNotice] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [noPermission, setNoPermission] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [twoFaUser, setTwoFaUser] = useState(null);
  const [twoFaQr, setTwoFaQr] = useState(null);
  const [twoFaManual, setTwoFaManual] = useState(null);
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [twoFaError, setTwoFaError] = useState(null);
  const [newUserForm, setNewUserForm] = useState({ email: '', username: '', password: '', role: 'user', active: true });
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        return window.localStorage.getItem('adminTheme') || 'dark';
      } catch {}
    }
    return 'dark';
  });
  const isLight = theme === 'light';
  const [builderSource, setBuilderSource] = useState('glpi');
  const [builderFields, setBuilderFields] = useState([]);
  const [builderColumns, setBuilderColumns] = useState([]);
  const [builderLoading, setBuilderLoading] = useState(false);
  const [builderSaving, setBuilderSaving] = useState(false);
  const [builderError, setBuilderError] = useState(null);
  const [builderNotice, setBuilderNotice] = useState(null);
  const [builderEdits, setBuilderEdits] = useState({});
  const [builderForm, setBuilderForm] = useState({ label: '', field: '' });
  const [dragFieldId, setDragFieldId] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem('adminTheme', theme);
      } catch {}
    }
  }, [theme]);

  const pageClass = isLight ? 'bg-slate-50 text-slate-800' : 'bg-[#0f0a1a] text-slate-300';
  const sidebarClass = isLight
    ? 'bg-white/90 border-slate-200 shadow-lg'
    : 'bg-[#140e21]/90 border-white/5 shadow-2xl shadow-black';
  const headerClass = isLight
    ? 'bg-white/80 border-slate-200'
    : 'bg-[#0f0a1a]/40 border-white/5';
  const panelClass = isLight
    ? 'bg-white border border-slate-200 shadow-sm'
    : 'bg-[#1a1425]/40 border border-white/5 shadow-xl';
  const inputClass = isLight
    ? 'bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400'
    : 'bg-white/5 border-white/5 text-slate-200 placeholder:text-slate-600';
  const ghostButtonClass = isLight
    ? 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
    : 'bg-white/5 border-white/10 text-slate-200 hover:bg-white/10';
  const subtleTextClass = isLight ? 'text-slate-500' : 'text-slate-400';

  // Busca dados reais do backend (rota: /auth/admin/users) e verifica /auth/me
  useEffect(() => {
    const prefix = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    const trim = (s) => (String(s || '').replace(/\/$/, ''));
    const root = trim(prefix);

    (async () => {
      try {
        // check current user
        const meRes = await fetch(`${root}/auth/me`, { credentials: 'include' });
        if (!meRes.ok) {
          setNoPermission(true);
          return;
        }
        const meJson = await meRes.json();
        const u = meJson && meJson.user ? meJson.user : meJson;
        setCurrentUser(u || null);
        const role = (u && (u.role || u.user_type)) || '';
        if (!role || (role !== 'super_admin' && role !== 'admin')) {
          setNoPermission(true);
          return;
        }

        // fetch users list
        setIsLoadingUsers(true);
        setUsersError(null);
        const res = await fetch(`${root}/auth/admin/users`, { credentials: 'include' });
        if (!res.ok) {
          setUsersError(`Erro ao carregar usuários (HTTP ${res.status})`);
          setUsers([]);
        } else {
          const json = await res.json();
          setUsers(json.users || json || []);
        }
      } catch (err) {
        setUsersError(err && err.message ? err.message : String(err));
        setUsers([]);
      } finally {
        setIsLoadingUsers(false);
      }
    })();
  }, []);

  const getApiRoot = () => {
    const prefix = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
    return String(prefix || '').replace(/\/$/, '');
  };

  const getUserDisplayName = (u) => {
    if (!u) return '';
    return String(u.full_name || u.username || u.name || u.email || '').trim();
  };

  const getUserRoleLabel = (u) => {
    const r = String((u && (u.role || u.user_type)) || '').toLowerCase();
    if (r === 'super_admin' || r === 'superadmin') return 'Super Admin';
    if (r === 'admin') return 'Admin';
    if (r === 'user') return 'User';
    return r ? r : '—';
  };

  const getInitials = (text) => {
    const s = String(text || '').trim();
    if (!s) return 'U';
    const parts = s.split(/\s+/).filter(Boolean);
    const a = parts[0] ? parts[0][0] : '';
    const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
    const initials = (a + b).toUpperCase();
    return initials || 'U';
  };

  const handleCreateUser = async () => {
    const root = getApiRoot();
    if (isSubmittingUser) return;

    setUsersNotice(null);
    if (isInviteMode) {
      if (!newUserForm.email || !newUserForm.username) {
        setUsersError('Nome e e-mail são obrigatórios para convite');
        return;
      }
    } else {
      if (!newUserForm.email || !newUserForm.password) {
        setUsersError('E-mail e senha são obrigatórios');
        return;
      }
    }
    try {
      setIsSubmittingUser(true);
      setUsersError(null);

      const url = isInviteMode ? `${root}/auth/admin/invite` : `${root}/auth/admin/users`;
      const body = isInviteMode
        ? {
            email: newUserForm.email,
            username: newUserForm.username,
            role: newUserForm.role,
          }
        : {
            email: newUserForm.email,
            username: newUserForm.username,
            password: newUserForm.password,
            role: newUserForm.role,
            active: newUserForm.active ? 1 : 0,
          };

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        setUsersError(`Falha ao criar usuário (HTTP ${res.status})`);
        return;
      }
      const json = await res.json();

      if (json && json.user) {
        setUsers(prev => {
          // evita duplicar no caso de usuário já existir
          const exists = prev.some(u => (u && u.id) && u.id === json.user.id);
          if (exists) return prev;
          return [json.user, ...prev];
        });
      }

      if (isInviteMode) {
        setUsersNotice(`Convite enviado para ${newUserForm.email}`);
      } else {
        setUsersNotice(`Usuário criado: ${newUserForm.email}`);
      }

      // fecha o modal com um pequeno delay para o usuário perceber o feedback
      setTimeout(() => {
        setIsModalOpen(false);
        setNewUserForm({ email: '', username: '', password: '', role: 'user', active: true });
        setIsInviteMode(false);
      }, 600);
    } catch (err) {
      setUsersError(err && err.message ? err.message : String(err));
    } finally {
      setIsSubmittingUser(false);
    }
  };

  const handleSaveProfile = async (updated) => {
    const root = getApiRoot();
    if (!updated || !updated.id) return;
    setProfileSaving(true);
    setProfileError(null);
    try {
      const body = { username: updated.username, role: updated.role, active: updated.active ? 1 : 0 };
      if (updated.password) body.password = updated.password;
      const res = await fetch(`${root}/auth/admin/users/${encodeURIComponent(updated.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        setProfileError(`Falha ao salvar usuário (HTTP ${res.status})`);
        return;
      }
      const json = await res.json();
      if (json && json.user) {
        setUsers(prev => prev.map(u => (u.id === json.user.id ? json.user : u)));
        setProfileUser(null);
      }
    } catch (err) {
      setProfileError(err && err.message ? err.message : String(err));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    if (!user) return;
    const root = getApiRoot();
    const userId = user.id || user.user_id;
    if (!userId) return;
    const nextActive = !(user.active || user.status === 'active');
    setIsLoadingUsers(true);
    setUsersError(null);
    try {
      const res = await fetch(`${root}/auth/admin/users/${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ active: nextActive ? 1 : 0 })
      });
      if (!res.ok) {
        setUsersError(`Falha ao atualizar usuário (HTTP ${res.status})`);
        return;
      }
      const json = await res.json();
      if (json && json.user) {
        setUsers(prev => prev.map(u => (u.id === json.user.id ? json.user : u)));
      }
    } catch (err) {
      setUsersError(err && err.message ? err.message : String(err));
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleUnlockUser = async (user) => {
    if (!user) return;
    const userId = user.id || user.user_id;
    if (!userId) return;
    const root = getApiRoot();
    setIsLoadingUsers(true);
    setUsersError(null);
    try {
      const res = await fetch(`${root}/auth/admin/users/${encodeURIComponent(userId)}/unlock`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!res.ok) {
        setUsersError(`Falha ao desbloquear usuário (HTTP ${res.status})`);
        return;
      }
      const json = await res.json();
      if (json && json.user) {
        setUsers(prev => prev.map(u => (u.id === json.user.id ? json.user : u)));
        if (profileUser && profileUser.id === json.user.id) setProfileUser(json.user);
      }
    } catch (err) {
      setUsersError(err && err.message ? err.message : String(err));
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleAdminTwoFaSetup = async (user, force = false) => {
    if (!user) return;
    const userId = user.id || user.user_id || user.email;
    if (!userId) return;
    const root = getApiRoot();
    setTwoFaError(null);
    setTwoFaLoading(true);
    try {
      const res = await fetch(`${root}/auth/admin/users/${encodeURIComponent(userId)}/2fa/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ force })
      });
      if (!res.ok) {
        const msg = `Falha ao gerar QR (HTTP ${res.status})`;
        setTwoFaError(msg);
        return;
      }
      const json = await res.json();
      setTwoFaUser(user);
      setTwoFaQr(json.qrDataUrl || null);
      setTwoFaManual(json.manualKey || null);
    } catch (e) {
      setTwoFaError(e && e.message ? e.message : String(e));
    } finally {
      setTwoFaLoading(false);
    }
  };

  const handleAdminTwoFaDisable = async (user) => {
    if (!user) return;
    const userId = user.id || user.user_id || user.email;
    if (!userId) return;
    const root = getApiRoot();
    setTwoFaError(null);
    setIsLoadingUsers(true);
    try {
      const res = await fetch(`${root}/auth/admin/users/${encodeURIComponent(userId)}/2fa/disable`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!res.ok) {
        setTwoFaError(`Falha ao desativar 2FA (HTTP ${res.status})`);
        return;
      }
      const json = await res.json();
      if (json && json.user) {
        setUsers(prev => prev.map(u => (u.id === json.user.id ? json.user : u)));
        if (profileUser && profileUser.id === json.user.id) setProfileUser(json.user);
      }
    } catch (e) {
      setTwoFaError(e && e.message ? e.message : String(e));
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const loadBuilderData = async () => {
    const root = getApiRoot();
    setBuilderLoading(true);
    setBuilderError(null);
    setBuilderNotice(null);
    try {
      const [fieldsRes, colsRes] = await Promise.all([
        fetch(`${root}/api/admin/tooltip-fields`, { credentials: 'include' }),
        fetch(`${root}/api/admin/glpi-columns`, { credentials: 'include' })
      ]);

      if (!fieldsRes.ok) {
        setBuilderError(`Falha ao carregar campos (HTTP ${fieldsRes.status})`);
      } else {
        const fieldsJson = await fieldsRes.json();
        setBuilderFields(Array.isArray(fieldsJson.fields) ? fieldsJson.fields : []);
      }

      if (!colsRes.ok) {
        setBuilderError(`Falha ao carregar colunas (HTTP ${colsRes.status})`);
      } else {
        const colsJson = await colsRes.json();
        setBuilderColumns(Array.isArray(colsJson.columns) ? colsJson.columns : []);
      }
    } catch (e) {
      setBuilderError(e && e.message ? e.message : String(e));
    } finally {
      setBuilderLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'Construtor') return;
    loadBuilderData();
  }, [activeTab]);

  const handleCreateBuilderField = async () => {
    const root = getApiRoot();
    const label = String(builderForm.label || '').trim();
    const field = String(builderForm.field || '').trim();
    if (!label || !field) {
      setBuilderError('Informe um rótulo e uma coluna GLPI.');
      return;
    }
    setBuilderLoading(true);
    setBuilderError(null);
    setBuilderNotice(null);
    try {
      const res = await fetch(`${root}/api/admin/tooltip-fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ label, field, source: builderSource, enabled: 1 })
      });
      if (!res.ok) {
        setBuilderError(`Falha ao criar campo (HTTP ${res.status})`);
        return;
      }
      const json = await res.json();
      if (json && json.field) {
        setBuilderFields(prev => [...prev, json.field]);
        setBuilderForm({ label: '', field: '' });
        setBuilderNotice('Campo adicionado com sucesso.');
      }
    } catch (e) {
      setBuilderError(e && e.message ? e.message : String(e));
    } finally {
      setBuilderLoading(false);
    }
  };

  const saveBuilderField = async (fieldId, payload) => {
    const root = getApiRoot();
    const res = await fetch(`${root}/api/admin/tooltip-fields/${encodeURIComponent(fieldId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      throw new Error(`Falha ao salvar campo (HTTP ${res.status})`);
    }
    const json = await res.json();
    return (json && json.field) ? json.field : null;
  };

  const handleSaveBuilderField = async (fieldId, overrides = {}) => {
    const current = builderFields.find(f => f.id === fieldId);
    if (!current) return;
    const draft = builderEdits[fieldId] || {};
    const payload = {
      label: overrides.label ?? draft.label ?? current.label,
      field: overrides.field ?? draft.field ?? current.field,
      enabled: overrides.enabled ?? draft.enabled ?? current.enabled,
    };
    if (overrides.sort_order != null) {
      payload.sort_order = overrides.sort_order;
    }
    setBuilderLoading(true);
    setBuilderError(null);
    setBuilderNotice(null);
    try {
      const updated = await saveBuilderField(fieldId, payload) || { ...current, ...payload };
      setBuilderFields(prev => prev.map(f => (f.id === fieldId ? updated : f)));
      setBuilderEdits(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
      setBuilderNotice('Campo atualizado.');
    } catch (e) {
      setBuilderError(e && e.message ? e.message : String(e));
    } finally {
      setBuilderLoading(false);
    }
  };

  const handleSaveAllBuilder = async () => {
    if (!builderFields.length) return;
    setBuilderSaving(true);
    setBuilderError(null);
    setBuilderNotice(null);
    try {
      const updates = await Promise.all(builderFields.map((field, idx) => {
        const payload = {
          label: field.label,
          field: field.field,
          enabled: field.enabled,
          sort_order: idx + 1
        };
        return saveBuilderField(field.id, payload).then(updated => updated || { ...field, ...payload });
      }));
      setBuilderFields(updates);
      setBuilderNotice('Alterações guardadas.');
    } catch (e) {
      setBuilderError(e && e.message ? e.message : String(e));
    } finally {
      setBuilderSaving(false);
    }
  };

  const reorderFields = (list, startId, endId) => {
    const startIndex = list.findIndex(f => f.id === startId);
    const endIndex = list.findIndex(f => f.id === endId);
    if (startIndex < 0 || endIndex < 0 || startIndex === endIndex) return list;
    const next = [...list];
    const [removed] = next.splice(startIndex, 1);
    next.splice(endIndex, 0, removed);
    return next;
  };

  const handleDropField = (targetId) => {
    if (!dragFieldId || dragFieldId === targetId) return;
    setBuilderFields(prev => reorderFields(prev, dragFieldId, targetId));
    setDragFieldId(null);
  };

  const handleDeleteBuilderField = async (fieldId) => {
    if (!fieldId) return;
    if (!window.confirm('Deseja remover este campo do tooltip?')) return;
    const root = getApiRoot();
    setBuilderLoading(true);
    setBuilderError(null);
    setBuilderNotice(null);
    try {
      const res = await fetch(`${root}/api/admin/tooltip-fields/${encodeURIComponent(fieldId)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        setBuilderError(`Falha ao remover campo (HTTP ${res.status})`);
        return;
      }
      setBuilderFields(prev => prev.filter(f => f.id !== fieldId));
      setBuilderNotice('Campo removido.');
    } catch (e) {
      setBuilderError(e && e.message ? e.message : String(e));
    } finally {
      setBuilderLoading(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const hay = String((u && (u.full_name || u.username || u.email)) || '').toLowerCase();
    return hay.includes((searchTerm || '').toLowerCase());
  });

  // calcula 'Acessos' como número de usuários com `last_login` nos últimos 30 dias
  const accessesCount = users.reduce((acc, u) => {
    try {
      if (!u) return acc;
      const candidate = u.last_login || u.lastLogin || u.last_access || u.lastSeen;
      if (!candidate) return acc;
      const d = new Date(candidate);
      if (isNaN(d)) return acc;
      const days = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
      if (days <= 30) return acc + 1;
      return acc;
    } catch (e) {
      return acc;
    }
  }, 0);

  const handleDeleteUser = async (id) => {
    if (!id) return;
    if (!window.confirm('Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.')) return;
    const root = getApiRoot();
    setIsLoadingUsers(true);
    setUsersError(null);
    try {
      const res = await fetch(`${root}/auth/admin/users/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        setUsersError(`Falha ao excluir usuário (HTTP ${res.status})`);
        return;
      }
      // remover do estado
      setUsers(prev => prev.filter(u => (u.id || u.user_id || String(u.email || u.username || '')) !== id && u.id !== id));
      // fechar modal de perfil se estava aberto para esse usuário
      if (profileUser && (profileUser.id === id || profileUser.user_id === id)) setProfileUser(null);
    } catch (err) {
      setUsersError(err && err.message ? err.message : String(err));
    } finally {
      setIsLoadingUsers(false);
    }
  };

  return (
    <div className={`min-h-screen ${pageClass} font-sans flex relative overflow-hidden`}>
      
      {/* BACKGROUND AURORA */}
      {!isLight && (
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-[-5%] left-[-5%] w-[40%] h-[40%] bg-indigo-600/5 blur-[120px] rounded-full"></div>
          <div className="absolute bottom-[-5%] right-[-5%] w-[30%] h-[30%] bg-fuchsia-600/5 blur-[100px] rounded-full"></div>
        </div>
      )}

      {/* SIDEBAR COMPACTA */}
      <aside className={`${isSidebarOpen ? 'w-56' : 'w-16'} ${sidebarClass} backdrop-blur-xl border-r transition-all duration-300 flex flex-col z-50 relative`}>
        <div className="p-4 flex items-center gap-2 overflow-hidden">
          <motion.div 
            whileHover={{ rotate: 90 }}
            className="w-8 h-8 bg-gradient-to-br from-indigo-600 to-violet-700 rounded-lg flex-shrink-0 flex items-center justify-center shadow-lg cursor-pointer"
          >
            <span className="text-white font-black text-lg italic tracking-tighter">B</span>
          </motion.div>
          {isSidebarOpen && (
            <span className="font-black text-white tracking-tighter text-lg uppercase">ADMIN</span>
          )}
        </div>

        <nav className="flex-1 px-2 py-4 space-y-1">
          <NavItem icon={<Home size={18}/>} label="Home" active={activeTab === 'Home'} isOpen={isSidebarOpen} onClick={() => setActiveTab('Home')} isLight={isLight} />
          <NavItem icon={<Users size={18}/>} label="Utilizadores" active={activeTab === 'Utilizadores'} isOpen={isSidebarOpen} onClick={() => setActiveTab('Utilizadores')} isLight={isLight} />
          <NavItem icon={<Briefcase size={18}/>} label="Projetos" active={activeTab === 'Projetos'} isOpen={isSidebarOpen} onClick={() => setActiveTab('Projetos')} isLight={isLight} />
          <NavItem icon={<Settings size={18}/>} label="Definições" active={activeTab === 'Configuracoes'} isOpen={isSidebarOpen} onClick={() => setActiveTab('Configuracoes')} isLight={isLight} />
          <NavItem icon={<Lock size={18}/>} label="2FA" active={activeTab === '2FA'} isOpen={isSidebarOpen} onClick={() => setActiveTab('2FA')} isLight={isLight} />
          <NavItem icon={<Wrench size={18}/>} label="Construtor" active={activeTab === 'Construtor'} isOpen={isSidebarOpen} onClick={() => setActiveTab('Construtor')} isLight={isLight} />
        </nav>

        <div className={`p-3 border-t ${isLight ? 'border-slate-200' : 'border-white/5'}`}>
          <div className={`flex items-center gap-2 p-2 rounded-xl border truncate ${isLight ? 'bg-slate-100 border-slate-200' : 'bg-white/5 border-white/5'}`}>
            <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-fuchsia-600 to-pink-600 flex items-center justify-center text-white font-black text-[10px]">
              {getInitials(getUserDisplayName(currentUser))}
            </div>
            {isSidebarOpen && (
              <div className="flex-1 truncate">
                <p className={`text-[10px] font-bold truncate ${isLight ? 'text-slate-800' : 'text-white'}`}>{getUserDisplayName(currentUser) || '—'}</p>
                <p className={`text-[8px] uppercase font-bold ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>{getUserRoleLabel(currentUser)}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 flex flex-col overflow-hidden relative z-10 text-base">
        
        {/* HEADER REDUZIDO */}
        <header className={`h-16 border-b ${headerClass} backdrop-blur-xl flex items-center justify-between px-6 sticky top-0 z-40`}>
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!isSidebarOpen)} className={`p-1.5 rounded-lg border ${isLight ? 'bg-slate-100 hover:bg-slate-200 text-slate-600 border-slate-200' : 'bg-white/5 hover:bg-white/10 text-slate-400 border-white/5'}`}>
              {isSidebarOpen ? <Menu size={16} /> : <X size={16} />}
            </button>
            <h1 className={`text-sm font-black tracking-tight flex items-center gap-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
              {activeTab} <Sparkles size={14} className={isLight ? 'text-amber-500' : 'text-yellow-400'} />
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setTheme(isLight ? 'dark' : 'light')}
              className={`px-3 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-all ${isLight ? 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100' : 'bg-white/5 border-white/10 text-slate-200 hover:bg-white/10'}`}
              title={isLight ? 'Mudar para Dark' : 'Mudar para White'}
            >
              {isLight ? <Moon size={12} /> : <Sun size={12} />}
            </button>
             {activeTab === 'Utilizadores' && (
               <motion.button 
                 whileHover={{ scale: 1.02 }}
                 whileTap={{ scale: 0.98 }}
                 onClick={() => {
                   setUsersError(null);
                   setUsersNotice(null);
                   setIsInviteMode(false);
                   setIsModalOpen(true);
                 }}
                 className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-lg text-[10px] font-black transition-all flex items-center gap-2 shadow-lg shadow-indigo-600/20 uppercase tracking-wider border border-white/10"
               >
                 <Plus size={14} strokeWidth={3} /> ADICIONAR
               </motion.button>
             )}
          </div>
        </header>

        {/* ÁREA DE CONTEÚDO */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <div className="max-w-7xl mx-auto space-y-6">
            {activeTab === '2FA' ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <StatCard label="Usuários" value={users.length ? String(users.length) : '—'} trend="Total" type="indigo" icon={<Users size={22}/>} isLight={isLight} />
                  <StatCard label="2FA Ativo" value={users.filter(u => u.two_factor_enabled).length || '—'} trend="Ativos" type="emerald" icon={<Lock size={22}/>} isLight={isLight} />
                  <StatCard label="2FA Inativo" value={users.filter(u => !u.two_factor_enabled).length || '—'} trend="Inativos" type="amber" icon={<Lock size={22}/>} isLight={isLight} />
                </div>

                <div className={`rounded-2xl p-3 flex flex-col md:flex-row gap-3 items-center backdrop-blur-md ${panelClass}`}>
                  <div className="relative flex-1 w-full group">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400" size={14} />
                    <input 
                      type="text" 
                      placeholder="Pesquisar usuário..." 
                      className={`w-full rounded-xl py-2 pl-9 pr-4 text-xs focus:outline-none focus:border-indigo-500 transition-all ${inputClass}`}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {twoFaError && (
                  <div className="rounded-lg bg-red-500/10 px-3 py-2 text-[11px] text-red-200">{twoFaError}</div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredUsers.map((user, idx) => {
                    const enabled = !!user.two_factor_enabled;
                    return (
                      <div key={user.id || user.user_id || idx} className={`rounded-3xl p-5 backdrop-blur-xl ${panelClass}`}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="truncate">
                            <div className="text-xs font-bold text-white truncate">{user.username || user.full_name || user.email}</div>
                            <div className={`text-[10px] truncate ${subtleTextClass}`}>{user.email}</div>
                          </div>
                          <div className={`text-[9px] font-black px-2 py-1 rounded-lg border ${enabled ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' : isLight ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-slate-800 text-slate-400 border-white/5'}`}>
                            {enabled ? '2FA ATIVO' : '2FA INATIVO'}
                          </div>
                        </div>
                        <div className={`text-[10px] mb-4 ${subtleTextClass}`}>
                          Último acesso: {(() => {
                            const raw = user.last_login || user.lastLogin || user.last_access || user.lastSeen || user.last_seen;
                            if (!raw) return '—';
                            const d = new Date(raw);
                            return isNaN(d) ? '—' : d.toLocaleString();
                          })()}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAdminTwoFaSetup(user, enabled)}
                            className="flex-1 py-2 text-[9px] font-black uppercase tracking-widest text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg"
                            disabled={twoFaLoading}
                          >
                            {enabled ? 'Resetar QR' : 'Gerar QR'}
                          </button>
                          <button
                            onClick={() => handleAdminTwoFaDisable(user)}
                            className={`flex-1 py-2 text-[9px] font-black uppercase tracking-widest rounded-lg border ${ghostButtonClass}`}
                            disabled={twoFaLoading || !enabled}
                          >
                            Desativar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : activeTab === 'Construtor' ? (
              <>
                <div className={`min-h-screen transition-colors duration-300 p-4 md:p-8 font-sans rounded-3xl ${isLight ? 'bg-slate-50 text-slate-900' : 'bg-slate-950 text-slate-100'}`}>
                  <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8">
                    {/* SIDEBAR: PREVIEW & CONTEXT */}
                    <aside className="lg:w-1/3 space-y-6">
                      <div className={`rounded-2xl shadow-sm border p-6 ${isLight ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-800'}`}>
                        <div className={`flex items-center gap-2 mb-4 font-semibold ${isLight ? 'text-blue-600' : 'text-blue-500'}`}>
                          <Eye size={20} />
                          <h2>Pré-visualização</h2>
                        </div>

                        <p className={`text-sm mb-6 ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                          É assim que o tooltip aparecerá para os utilizadores ao passarem o rato sobre um ativo no mapa.
                        </p>

                        <div className={`relative h-64 rounded-xl overflow-hidden border flex items-center justify-center ${isLight ? 'bg-slate-100 border-slate-200' : 'bg-slate-800 border-slate-700'}`}>
                          <div className={`absolute inset-0 opacity-20 bg-[url('https://www.google.com/maps/vt/pb=!1m4!1m3!1i12!2i2345!3i1234!2m3!1e0!2sm!3i420120488!3m8!2spt-PT!3sPT!5e1105!12m4!1e68!2m2!1sset!2sRoadmap!4e0!5m1!1e0!23i4111425')] bg-center ${isLight ? '' : 'invert grayscale'}`}></div>

                          <div className="relative z-10 animate-in fade-in zoom-in duration-300">
                            <div className={`shadow-xl rounded-lg border w-48 overflow-hidden ${isLight ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-700'}`}>
                              <div className="bg-blue-600 p-2 text-white text-xs font-bold flex items-center gap-1">
                                <MapPin size={12} /> Ativo GLPI #4521
                              </div>
                              <div className="p-3 space-y-2">
                                {builderFields.filter(f => f.enabled).length > 0 ? (
                                  builderFields.filter(f => f.enabled).map(f => (
                                    <div key={f.id} className={`border-b last:border-0 pb-1 ${isLight ? 'border-slate-50' : 'border-slate-800'}`}>
                                      <span className={`block text-[10px] uppercase tracking-wider font-bold ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>{f.label}</span>
                                      <span className={`text-xs ${isLight ? 'text-slate-700' : 'text-slate-300'}`}>Valor Exemplo</span>
                                    </div>
                                  ))
                                ) : (
                                  <span className={`text-[10px] italic ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>Nenhum campo selecionado</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={`rounded-2xl p-6 border ${isLight ? 'bg-blue-50 border-blue-100' : 'bg-blue-950/20 border-blue-900/40'}`}>
                        <div className="flex gap-3">
                          <Info className={`shrink-0 ${isLight ? 'text-blue-500' : 'text-blue-500'}`} size={20} />
                          <p className={`text-sm leading-relaxed ${isLight ? 'text-blue-800' : 'text-blue-200'}`}>
                           Os nomes alterados nos campos serão exibidos no tooltip. Utilize o botão de arrastar (⋮⋮) para reordenar os campos conforme desejado.
                          </p>
                        </div>
                      </div>
                    </aside>

                    {/* MAIN CONTENT: CONFIGURATION */}
                    <main className="lg:w-2/3 space-y-4">
                      <div className={`rounded-2xl shadow-sm border overflow-hidden ${isLight ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-800'}`}>
                        <div className={`px-6 py-4 border-b flex justify-between items-center ${isLight ? 'bg-slate-50/50 border-slate-100' : 'bg-slate-900/50 border-slate-800'}`}>
                          <div>
                            <h1 className={`text-lg font-bold ${isLight ? 'text-slate-800' : ''}`}>Campos do Tooltip</h1>
                            <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>Configure quais os dados serão exibidos no mapa.</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={handleSaveAllBuilder}
                              disabled={builderSaving}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                                builderSaving
                                  ? isLight
                                    ? 'bg-emerald-100 text-emerald-600'
                                    : 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/50'
                                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                              }`}
                            >
                              {builderSaving ? <CheckCircle2 size={18} /> : <Save size={18} />}
                              {builderSaving ? 'Guardado!' : 'Guardar Alterações'}
                            </button>
                          </div>
                        </div>

                        {builderError && (
                          <div className={`px-6 py-3 text-sm border-b ${isLight ? 'text-red-600 bg-red-50 border-red-100' : 'text-red-300 bg-red-950/30 border-red-900/40'}`}>{builderError}</div>
                        )}
                        {builderNotice && (
                          <div className={`px-6 py-3 text-sm border-b ${isLight ? 'text-emerald-600 bg-emerald-50 border-emerald-100' : 'text-emerald-300 bg-emerald-950/30 border-emerald-900/40'}`}>{builderNotice}</div>
                        )}

                        <div className="p-4 space-y-2">
                          {builderFields.map((field) => (
                            <div
                              key={field.id}
                              draggable
                              onDragStart={() => setDragFieldId(field.id)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => handleDropField(field.id)}
                              className={`flex items-center gap-3 p-3 rounded-xl border transition-all group ${
                                field.enabled
                                  ? isLight
                                    ? 'bg-white border-slate-200 hover:border-blue-300'
                                    : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                                  : isLight
                                    ? 'bg-slate-50 border-slate-100 opacity-75'
                                    : 'opacity-60 bg-slate-900 border-slate-800'
                              }`}
                            >
                              <div className={`cursor-grab p-1 transition-colors ${isLight ? 'text-slate-300 hover:text-slate-400' : 'text-slate-600 hover:text-slate-400'}`}>
                                <GripVertical size={18} />
                              </div>

                              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input
                                  type="text"
                                  value={field.label || ''}
                                  onChange={(e) => {
                                    const nextLabel = e.target.value;
                                    setBuilderFields(prev => prev.map(f => (f.id === field.id ? { ...f, label: nextLabel } : f)));
                                    setBuilderEdits(prev => ({ ...prev, [field.id]: { ...(prev[field.id] || {}), label: nextLabel } }));
                                  }}
                                  className={`rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-colors ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-800' : 'bg-slate-800 border-slate-700 text-slate-200'}`}
                                  placeholder="Rótulo de exibição"
                                />
                                <select
                                  value={field.field || ''}
                                  onChange={(e) => {
                                    const nextField = e.target.value;
                                    setBuilderFields(prev => prev.map(f => (f.id === field.id ? { ...f, field: nextField } : f)));
                                    setBuilderEdits(prev => ({ ...prev, [field.id]: { ...(prev[field.id] || {}), field: nextField } }));
                                  }}
                                  className={`rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-colors ${isLight ? 'bg-slate-50 border border-slate-200 text-slate-800' : 'bg-slate-800 border-slate-700 text-slate-200'}`}
                                >
                                  {builderColumns.map(col => {
                                    const colName = col?.name || col;
                                    const colLabel = col?.label || colName;
                                    return (
                                      <option key={colName} value={colName} className={isLight ? '' : 'bg-slate-800'}>{colLabel}</option>
                                    );
                                  })}
                                </select>
                              </div>

                              <div className="flex items-center gap-4 px-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <div className="relative">
                                    <input
                                      type="checkbox"
                                      className="sr-only"
                                      checked={!!field.enabled}
                                      onChange={() => {
                                        const nextEnabled = field.enabled ? 0 : 1;
                                        setBuilderFields(prev => prev.map(f => (f.id === field.id ? { ...f, enabled: nextEnabled } : f)));
                                        handleSaveBuilderField(field.id, { enabled: nextEnabled });
                                      }}
                                    />
                                    <div className={`w-10 h-5 rounded-full transition-colors ${field.enabled ? 'bg-blue-600' : isLight ? 'bg-slate-300' : 'bg-slate-700'}`}></div>
                                    <div className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${field.enabled ? 'translate-x-5' : ''}`}></div>
                                  </div>
                                  <span className={`text-xs font-medium hidden sm:block ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>
                                    {field.enabled ? 'Ativo' : 'Inativo'}
                                  </span>
                                </label>

                                <button
                                  onClick={() => handleDeleteBuilderField(field.id)}
                                  className={`p-2 rounded-lg transition-colors ${isLight ? 'text-slate-400 hover:text-red-500 hover:bg-red-50' : 'text-slate-500 hover:text-red-400 hover:bg-red-400/10'}`}
                                  title="Remover campo"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className={`p-6 border-t ${isLight ? 'bg-slate-50 border-slate-100' : 'bg-slate-900/80 border-slate-800'}`}>
                          <h3 className={`text-sm font-bold mb-4 flex items-center gap-2 ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>
                            <Plus size={16} /> Adicionar Novo Campo
                          </h3>
                          <div className="flex flex-col md:flex-row gap-3">
                            <input
                              type="text"
                              placeholder="Ex: Rótulo (ex: Localização)"
                              className={`flex-1 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none shadow-sm transition-colors ${isLight ? 'bg-white border border-slate-200 text-slate-800' : 'bg-slate-800 border-slate-700 text-slate-200'}`}
                              value={builderForm.label}
                              onChange={(e) => setBuilderForm(prev => ({ ...prev, label: e.target.value }))}
                            />
                            <select
                              className={`flex-1 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none shadow-sm transition-colors ${isLight ? 'bg-white border border-slate-200 text-slate-800' : 'bg-slate-800 border-slate-700 text-slate-200'}`}
                              value={builderForm.field}
                              onChange={(e) => setBuilderForm(prev => ({ ...prev, field: e.target.value }))}
                            >
                              <option value="" className={isLight ? '' : 'bg-slate-800'}>Selecione o campo técnico...</option>
                              {builderColumns.map(col => {
                                const colName = col?.name || col;
                                const colLabel = col?.label || colName;
                                return (
                                  <option key={colName} value={colName} className={isLight ? '' : 'bg-slate-800'}>{colLabel}</option>
                                );
                              })}
                            </select>
                            <button
                              onClick={handleCreateBuilderField}
                              className="bg-emerald-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-emerald-700 transition-colors shadow-sm flex items-center justify-center gap-2"
                              disabled={builderLoading}
                            >
                              <Plus size={18} /> Adicionar
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-between items-center px-2 py-4">
                        <div className={`flex items-center gap-2 text-xs ${isLight ? 'text-slate-400' : 'text-slate-500'}`}>
                          <Settings2 size={14} />
                          <span>ID da Configuração: TOOLTIP_MAP_V2_DARK</span>
                        </div>
                        <button
                          className={`text-sm font-medium transition-colors ${isLight ? 'text-slate-500 hover:text-blue-600' : 'text-slate-500 hover:text-blue-400'}`}
                          onClick={loadBuilderData}
                        >
                          Repor definições originais
                        </button>
                      </div>
                    </main>
                  </div>
                </div>
              </>
            ) : (
              <>
            
            {/* STATS - MENORES */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              <StatCard label="Total" value={users.length ? String(users.length) : '—'} trend={`${users.length} usuários`} type="indigo" icon={<Users size={22}/>} isLight={isLight} />
              <StatCard label="Ativos" value={users.filter(u => u.active || u.status === 'active').length || '—'} trend={`${users.length ? Math.round((users.filter(u => u.active || u.status === 'active').length / users.length) * 100) : 0}%`} type="emerald" icon={<CheckCircle2 size={22}/>} isLight={isLight} />
              <StatCard label="Admins" value={users.filter(u => (u.role || u.user_type) === 'admin' || (u.role || u.user_type) === 'super_admin').length || '—'} trend={`${users.length ? Math.round((users.filter(u => (u.role || u.user_type) === 'admin' || (u.role || u.user_type) === 'super_admin').length / users.length) * 100) : 0}%`} type="fuchsia" icon={<Shield size={22}/>} isLight={isLight} />
              <StatCard label="Acessos" value={accessesCount || '—'} trend={`${users.length ? Math.round((accessesCount / users.length) * 100) : 0}%`} type="amber" icon={<Activity size={22}/>} isLight={isLight} />
            </div>

            {/* BARRA DE BUSCA COMPACTA */}
            <div className={`rounded-2xl p-3 flex flex-col md:flex-row gap-3 items-center backdrop-blur-md ${panelClass}`}>
              <div className="relative flex-1 w-full group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-indigo-400" size={14} />
                <input 
                  type="text" 
                  placeholder="Pesquisar..." 
                  className={`w-full rounded-xl py-2 pl-9 pr-4 text-xs focus:outline-none focus:border-indigo-500 transition-all ${inputClass}`}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <FilterSelect label="Função" isLight={isLight} />
                <div className="h-6 w-[1px] bg-white/10"></div>
                <div className={`flex rounded-xl p-1 border ${isLight ? 'bg-slate-100 border-slate-200' : 'bg-white/5 border-white/5'}`}>
                  <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500'}`}><LayoutGrid size={14} /></button>
                  <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500'}`}><List size={14} /></button>
                </div>
              </div>
            </div>

            {/* GRID DE UTILIZADORES MENORES */}
            <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'} gap-4`}>
              <AnimatePresence mode="popLayout">
                {isLoadingUsers && (
                  <div className={`col-span-full text-center py-8 ${subtleTextClass}`}>Carregando usuários…</div>
                )}
                {!isLoadingUsers && usersError && (
                  <div className="col-span-full text-center py-8 text-red-500">{usersError}</div>
                )}
                {!isLoadingUsers && !usersError && filteredUsers.map((user, idx) => (
                  <UserCard 
                    key={user.id || user.user_id || `${idx}`} 
                    user={user} 
                    index={idx}
                    onToggleStatus={() => handleToggleActive(user)} 
                    onViewProfile={(u) => setProfileUser(u)}
                    onDelete={() => handleDeleteUser(user.id || user.user_id || user.email || user.username)}
                    onUnlock={() => handleUnlockUser(user)}
                    isLight={isLight}
                  />
                ))}
              </AnimatePresence>
            </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Modal de permissão */}
      <AnimatePresence>
        {noPermission && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/60" />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="relative w-full max-w-lg bg-white rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-900">SEM PERMISSÃO</h2>
              <p className="mt-2 text-sm text-gray-700">Seu usuário não tem permissão para acessar o Painel Admin.</p>
              <div className="mt-6 flex justify-end">
                <button onClick={() => { window.location.href = '/'; }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg">Voltar</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL ADICIONAR UTILIZADOR */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className={`relative w-full max-w-md rounded-[2rem] overflow-hidden p-6 ${isLight ? 'bg-white border border-slate-200 shadow-xl' : 'bg-[#140e21] border border-white/10 shadow-2xl'}`}
            >
              <AnimatePresence>
                {isSubmittingUser && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 z-10 bg-black/55 backdrop-blur-sm flex items-center justify-center"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-10 h-10 rounded-full border-2 border-white/15 border-t-indigo-400 animate-spin" />
                      <div className={`text-xs font-bold tracking-wide ${isLight ? 'text-slate-700' : 'text-slate-200'}`}>
                        {isInviteMode ? 'Enviando convite…' : 'Criando usuário…'}
                      </div>
                      <div className={`text-[10px] ${subtleTextClass}`}>Aguarde um instante.</div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className={`text-lg font-black flex items-center gap-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
                    <UserPlus size={18} className={isLight ? 'text-indigo-600' : 'text-indigo-400'} /> Adicionar Utilizador
                  </h2>
                  <p className={`text-[10px] uppercase tracking-widest font-bold ${subtleTextClass}`}>Gerenciamento de Sistema</p>
                </div>
                <button onClick={() => setIsModalOpen(false)} className={`p-2 rounded-full transition-colors ${isLight ? 'hover:bg-slate-100 text-slate-500 hover:text-slate-800' : 'hover:bg-white/5 text-slate-500 hover:text-white'}`}>
                  <X size={20} />
                </button>
              </div>

              {/* FORMULÁRIO DE CRIAÇÃO DE USUÁRIO */}
              <div className="space-y-4">
                <label className={`flex items-center gap-2 text-xs ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>
                  <input
                    type="checkbox"
                    checked={isInviteMode}
                    onChange={(e) => setIsInviteMode(e.target.checked)}
                  />
                  Cadastro via convite
                </label>
                {isInviteMode && (
                  <div className={`rounded-xl border px-3 py-2 text-[11px] ${isLight ? 'border-slate-200 bg-slate-50 text-slate-600' : 'border-white/10 bg-white/5 text-slate-300'}`}>
                    Um e-mail será enviado para o usuário criar a senha e entrar automaticamente no sistema.
                  </div>
                )}
                <div>
                  <label className={`text-xs mb-1 block ${subtleTextClass}`}>Nome</label>
                  <input value={newUserForm.username} onChange={(e) => setNewUserForm(prev => ({ ...prev, username: e.target.value }))} className={`w-full px-3 py-2 rounded-lg border ${inputClass}`} placeholder="Nome completo ou usuário" />
                </div>
                <div>
                  <label className={`text-xs mb-1 block ${subtleTextClass}`}>E-mail</label>
                  <input value={newUserForm.email} onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))} className={`w-full px-3 py-2 rounded-lg border ${inputClass}`} placeholder="email@exemplo.com" />
                </div>
                {!isInviteMode && (
                  <div>
                    <label className={`text-xs mb-1 block ${subtleTextClass}`}>Senha</label>
                    <input type="password" value={newUserForm.password} onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))} className={`w-full px-3 py-2 rounded-lg border ${inputClass}`} placeholder="Senha" />
                  </div>
                )}
                <div className="flex gap-3 items-center">
                  <select value={newUserForm.role} onChange={(e) => setNewUserForm(prev => ({ ...prev, role: e.target.value }))} className={`px-3 py-2 rounded-lg border ${inputClass}`}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                  {!isInviteMode && (
                    <label className={`ml-auto flex items-center gap-2 text-xs ${isLight ? 'text-slate-600' : 'text-slate-300'}`}><input type="checkbox" checked={newUserForm.active} onChange={(e) => setNewUserForm(prev => ({ ...prev, active: e.target.checked }))} /> Ativo</label>
                  )}
                </div>
                {usersNotice && <div className="rounded-lg bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-200">{usersNotice}</div>}
                {usersError && <div className="rounded-lg bg-red-500/10 px-3 py-2 text-[11px] text-red-200">{usersError}</div>}
              </div>

              <div className="mt-8 flex gap-3">
                <button onClick={() => setIsModalOpen(false)} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors ${isLight ? 'text-slate-600 hover:text-slate-900' : 'text-slate-400 hover:text-white'}`}>Cancelar</button>
                <button
                  onClick={handleCreateUser}
                  disabled={isSubmittingUser}
                  className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg transition-all border border-white/10 ${isSubmittingUser ? 'bg-indigo-600/60 text-white/80 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/20'}`}
                >
                  {isSubmittingUser ? (isInviteMode ? 'Enviando…' : 'Criando…') : (isInviteMode ? 'Enviar Convite' : 'Criar Utilizador')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL PERFIL / EDITAR USUÁRIO */}
      <AnimatePresence>
        {profileUser && (
          <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/60" onClick={() => setProfileUser(null)} />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className={`relative w-full max-w-lg rounded-2xl p-6 ${isLight ? 'bg-white border border-slate-200' : 'bg-[#0b0711] border border-white/10'}`}>
              <h2 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`}>Perfil: {(profileUser.full_name || profileUser.username || profileUser.email || 'Usuário')}</h2>
              <div className="mt-4 space-y-3">
                <div>
                  <label className={`text-xs mb-1 block ${subtleTextClass}`}>Nome</label>
                  <input value={profileUser.username || profileUser.full_name || ''} onChange={(e) => setProfileUser(prev => ({ ...prev, username: e.target.value }))} className={`w-full px-3 py-2 rounded-lg border ${inputClass}`} />
                </div>
                <div>
                  <label className={`text-xs mb-1 block ${subtleTextClass}`}>E-mail</label>
                  <input value={profileUser.email || ''} readOnly className={`w-full px-3 py-2 rounded-lg border ${isLight ? 'bg-slate-100 border-slate-200 text-slate-700' : 'bg-[#0f0a1a]/40 border border-white/5 text-slate-300'}`} />
                </div>
                <div className="flex gap-2 items-center">
                  <select value={profileUser.role || profileUser.user_type || 'user'} onChange={(e) => setProfileUser(prev => ({ ...prev, role: e.target.value }))} className={`px-3 py-2 rounded-lg border ${inputClass}`}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                  <label className={`flex items-center gap-2 ml-auto text-xs ${isLight ? 'text-slate-600' : 'text-slate-300'}`}><input type="checkbox" checked={!!(profileUser.active || profileUser.status === 'active')} onChange={(e) => setProfileUser(prev => ({ ...prev, active: e.target.checked }))} /> Ativo</label>
                </div>
                <div>
                  <label className={`text-xs mb-1 block ${subtleTextClass}`}>Nova senha (opcional)</label>
                  <input type="password" value={profileUser.password || ''} onChange={(e) => setProfileUser(prev => ({ ...prev, password: e.target.value }))} className={`w-full px-3 py-2 rounded-lg border ${inputClass}`} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`text-xs mb-1 block ${subtleTextClass}`}>Último acesso</label>
                    <div className={`px-3 py-2 rounded-lg border text-xs ${isLight ? 'bg-slate-100 border-slate-200 text-slate-700' : 'bg-[#0f0a1a]/40 border border-white/5 text-slate-300'}`}>
                      {(() => {
                        const raw = profileUser.last_login || profileUser.lastLogin || profileUser.last_access || profileUser.lastSeen || profileUser.last_seen;
                        if (!raw) return '—';
                        const d = new Date(raw);
                        return isNaN(d) ? '—' : d.toLocaleString();
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className={`text-xs mb-1 block ${subtleTextClass}`}>Tentativas falhas</label>
                    <div className={`px-3 py-2 rounded-lg border text-xs ${isLight ? 'bg-slate-100 border-slate-200 text-slate-700' : 'bg-[#0f0a1a]/40 border border-white/5 text-slate-300'}`}>
                      {Number(profileUser.failed_attempts || 0)}
                    </div>
                  </div>
                </div>
                <div>
                  <label className={`text-xs mb-1 block ${subtleTextClass}`}>Bloqueio</label>
                  <div className={`px-3 py-2 rounded-lg border text-xs ${isLight ? 'bg-slate-100 border-slate-200 text-slate-700' : 'bg-[#0f0a1a]/40 border border-white/5 text-slate-300'}`}>
                    {(() => {
                      const raw = profileUser.locked_until || profileUser.lockedUntil;
                      if (!raw) return '—';
                      const d = new Date(raw);
                      return isNaN(d) ? '—' : `Até ${d.toLocaleString()}`;
                    })()}
                  </div>
                </div>
                {profileError && <div className="text-red-400">{profileError}</div>}
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={() => setProfileUser(null)} className="px-4 py-2 rounded-lg bg-white/5">Fechar</button>
                <button
                  onClick={() => handleToggleActive(profileUser)}
                  className="px-4 py-2 rounded-lg bg-white/5 text-slate-200"
                >
                  {profileUser.active ? 'Desativar' : 'Ativar'}
                </button>
                {(() => {
                  const raw = profileUser.locked_until || profileUser.lockedUntil;
                  const d = raw ? new Date(raw) : null;
                  const locked = (!!d && !isNaN(d) && d.getTime() > Date.now()) || Number(profileUser.failed_attempts || 0) >= 5 || (!profileUser.active);
                  return locked ? (
                    <button
                      onClick={() => handleUnlockUser(profileUser)}
                      className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-200"
                    >
                      Desbloquear
                    </button>
                  ) : null;
                })()}
                <button onClick={() => handleSaveProfile(profileUser)} disabled={profileSaving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg">{profileSaving ? 'Salvando...' : 'Salvar'}</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL 2FA QR */}
      <AnimatePresence>
        {twoFaUser && (twoFaQr || twoFaManual) && (
          <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/60" onClick={() => { setTwoFaUser(null); setTwoFaQr(null); setTwoFaManual(null); }} />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="relative w-full max-w-md bg-[#0b0711] border border-white/10 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white">2FA - {twoFaUser.username || twoFaUser.email}</h2>
              <p className="text-xs text-slate-400 mt-1">Escaneie o QR no Microsoft Authenticator e confirme no app.</p>
              <div className="mt-4 flex flex-col items-center gap-4">
                {twoFaQr && <img src={twoFaQr} alt="QR 2FA" className="w-48 h-48 rounded-lg border border-white/10" />}
                {twoFaManual && (
                  <div className="text-[11px] text-slate-300">
                    Chave manual: <span className="font-mono text-white">{twoFaManual}</span>
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={() => { setTwoFaUser(null); setTwoFaQr(null); setTwoFaManual(null); }} className="px-4 py-2 rounded-lg bg-white/5">Fechar</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #332a4d; border-radius: 10px; }
      `}</style>
    </div>
  );
};

// --- COMPONENTES AUXILIARES ---

const NavItem = ({ icon, label, active, isOpen, onClick, isLight }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all group relative ${active ? (isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-600/10 text-indigo-400') : (isLight ? 'text-slate-600 hover:bg-slate-100 hover:text-slate-900' : 'text-slate-500 hover:bg-white/5 hover:text-white')}`}
  >
    <span className={active ? 'text-indigo-400' : ''}>{icon}</span>
    {isOpen && <span className="text-xs font-bold uppercase tracking-widest">{label}</span>}
    {active && isOpen && <div className="ml-auto w-1 h-1 rounded-full bg-indigo-500 shadow-[0_0_8px_#6366f1]"></div>}
  </button>
);

const StatCard = ({ label, value, trend, type, icon, isLight }) => {
  const themes = {
    indigo: { hex: "#818CF880", lightHex: "#EEF2FF", iconColor: "text-indigo-400" },
    emerald: { hex: "#31BE8C80", lightHex: "#ECFDF3", iconColor: "text-emerald-400" },
    fuchsia: { hex: "#E879F980", lightHex: "#FDF4FF", iconColor: "text-fuchsia-400" },
    amber: { hex: "#FBBF2480", lightHex: "#FFFBEB", iconColor: "text-amber-400" }
  };
  const current = themes[type];

  return (
    <motion.div 
      whileHover="hover" initial="initial"
      variants={{
        initial: { backgroundColor: isLight ? "#ffffff" : "rgba(26, 20, 37, 0.4)", y: 0 },
        hover: { backgroundColor: isLight ? current.lightHex : current.hex, y: -4 }
      }}
      className={`rounded-3xl p-4 group relative overflow-hidden backdrop-blur-xl shadow-lg border ${isLight ? 'border-slate-200' : 'border-white/5'}`}
    >
      <div className="flex justify-between items-start mb-4 relative z-10">
        <div className={`p-3 rounded-xl border ${current.iconColor} group-hover:text-white transition-all ${isLight ? 'bg-slate-100 border-slate-200' : 'bg-white/5 border-white/10'}`}>
          {icon}
        </div>
        <div className={`text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-tighter ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-white'}`}>
           {trend}
        </div>
      </div>
      <div className="relative z-10">
        <p className={`text-[9px] font-bold uppercase tracking-widest mb-1 ${isLight ? 'text-slate-500' : 'text-slate-500 group-hover:text-white/70'}`}>{label}</p>
        <h3 className={`text-2xl font-black tracking-tighter ${isLight ? 'text-slate-900' : 'text-white'}`}>{value}</h3>
      </div>
      <div className={`absolute bottom-0 left-0 w-full h-1 overflow-hidden ${isLight ? 'bg-slate-200' : 'bg-black/20'}`}>
        <motion.div variants={{ initial: { x: "-100%" }, hover: { x: "0%" } }} className={`h-full w-full ${isLight ? 'bg-slate-300' : 'bg-white/40'}`} />
      </div>
    </motion.div>
  );
};

const UserCard = forwardRef(({ user, onToggleStatus, onViewProfile, onDelete, onUnlock, index, isLight }, ref) => {
  const typeConfig = {
    super_admin: { label: "Super Admin", color: "text-fuchsia-400", bg: "from-fuchsia-600 to-pink-600" },
    admin: { label: "Admin", color: "text-indigo-400", bg: "from-indigo-600 to-violet-600" },
    user: { label: "User", color: "text-cyan-400", bg: "from-blue-600 to-cyan-600" }
  };
  const roleKey = (user && (user.user_type || user.role)) || 'user';
  const config = typeConfig[roleKey] || typeConfig.user;

  const displayName = (user && (user.full_name || user.username || user.email)) || 'Usuário';
  const lockedUntilRaw = user && (user.locked_until || user.lockedUntil);
  const lockedUntilDate = lockedUntilRaw ? new Date(lockedUntilRaw) : null;
  const isLocked = (!!lockedUntilDate && !isNaN(lockedUntilDate.getTime()) && lockedUntilDate.getTime() > Date.now()) || ((user && user.failed_attempts >= 5) && !(user.active || user.status === 'active'));
  const statusActive = !isLocked && ((user && (user.status || (user.active ? 'active' : ''))) || '').toLowerCase() === 'active';

  // último acesso: tenta vários campos comuns e formata como "Há 2h", "Há 3d", ou 'agora'
  const lastSeenRaw = user && (user.last_login || user.lastLogin || user.last_access || user.lastSeen || user.last_seen || user.lastAccess || user.lastSeenAt);
  const lastSeenDate = lastSeenRaw ? new Date(lastSeenRaw) : null;
  const lastSeenText = (() => {
    if (!lastSeenDate || isNaN(lastSeenDate.getTime())) return '—';
    const diff = Date.now() - lastSeenDate.getTime();
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return 'agora';
    const min = Math.floor(sec / 60);
    if (min < 60) return `Há ${min}m`;
    const hours = Math.floor(min / 60);
    if (hours < 24) return `Há ${hours}h`;
    const days = Math.floor(hours / 24);
    return `Há ${days}d`;
  })();

  return (
    <motion.div 
      ref={ref} layout 
      initial={{ opacity: 0, y: 10 }} 
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ 
        y: -12, 
        scale: 1.02,
        transition: { type: "spring", stiffness: 300, damping: 20 }
      }}
      className={`rounded-3xl p-5 transition-all group backdrop-blur-xl relative overflow-hidden ${isLight ? 'bg-white border border-slate-200 shadow-md hover:shadow-lg hover:border-indigo-200' : 'bg-[#1a1425]/40 border border-white/5 shadow-xl hover:shadow-[0_20px_50px_rgba(0,0,0,0.6)] hover:border-indigo-500/40'}`}
    >
      {/* Glow de fundo que aparece suavemente no hover */}
      <div className={`absolute -inset-1 bg-gradient-to-br ${config.bg} opacity-0 group-hover:opacity-[0.05] blur-2xl transition-opacity duration-500`}></div>

      <div className="flex justify-between items-start mb-5 relative z-10">
        <div className="flex items-center gap-3 truncate">
          <motion.div 
            whileHover={{ rotate: 10 }}
            className={`w-10 h-10 bg-gradient-to-br ${config.bg} rounded-xl flex items-center justify-center text-white font-black text-lg shadow-lg group-hover:shadow-indigo-500/20 transition-all`}
          >
            {String(displayName).charAt(0)}
          </motion.div>
          <div className="truncate">
            <h3 className={`text-xs font-bold truncate group-hover:text-indigo-500 transition-colors ${isLight ? 'text-slate-900' : 'text-white'}`}>{displayName}</h3>
            <span className={`text-[8px] font-black uppercase tracking-widest ${config.color} flex items-center gap-1`}>
              <Shield size={10} strokeWidth={2.5} /> {config.label}
            </span>
          </div>
        </div>
        <button className={`transition-colors ${isLight ? 'text-slate-500 hover:text-slate-800' : 'text-slate-600 hover:text-white'}`}><MoreVertical size={16} /></button>
      </div>

      <div className={`rounded-xl p-2.5 mb-5 flex items-center gap-2 truncate transition-colors relative z-10 border ${isLight ? 'bg-slate-100 border-slate-200' : 'bg-white/5 border-white/5 group-hover:bg-white/10'}`}>
        <Mail size={12} className="text-indigo-400 flex-shrink-0" />
        <span className={`text-[10px] truncate font-bold ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>{user.email || ''}</span>
      </div>

      <div className={`grid grid-cols-2 gap-2 mb-5 py-3 border-y relative z-10 transition-colors ${isLight ? 'border-slate-200' : 'border-white/5 group-hover:border-white/10'}`}>
        <div>
          <p className={`text-[8px] font-bold uppercase mb-0.5 ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>Atividade</p>
          <p className={`text-[10px] font-black truncate ${isLight ? 'text-slate-800' : 'text-slate-200 group-hover:text-white'}`}>{lastSeenText}</p>
        </div>
        <div className="text-right">
          <p className={`text-[8px] font-bold uppercase mb-0.5 ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>Status</p>
          {isLocked ? (
            <div className="inline-flex items-center gap-1 text-[8px] font-black px-2 py-0.5 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300">
              <div className="w-1 h-1 rounded-full bg-red-400 animate-pulse" />
              BLOQUEADO
            </div>
          ) : (
            <div className={`inline-flex items-center gap-1 text-[8px] font-black px-2 py-0.5 rounded-lg border transition-all ${statusActive ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : isLight ? 'text-slate-500 border-slate-200' : 'text-slate-500 border-white/5'}`}>
              <div className={`w-1 h-1 rounded-full ${statusActive ? 'bg-emerald-500 animate-pulse' : isLight ? 'bg-slate-400' : 'bg-slate-600'}`}></div>
              {String((user && (user.status || (user.active ? 'active' : 'inactive'))) || 'inactive').toUpperCase()}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => onViewProfile && onViewProfile(user)} className={`text-[9px] font-black uppercase tracking-widest flex items-center gap-1 transition-colors ${isLight ? 'text-indigo-600 hover:text-indigo-700' : 'text-indigo-400 group-hover:text-indigo-300'}`}>
            Ver Perfil <ExternalLink size={10} strokeWidth={2.5} />
          </button>
          <button onClick={() => onDelete && onDelete(user)} className={`text-[9px] font-black uppercase tracking-widest flex items-center gap-1 transition-colors ${isLight ? 'text-red-500 hover:text-red-600' : 'text-red-400 hover:text-red-300'}`}>
            Excluir <Trash size={12} />
          </button>
          {isLocked && (
            <button onClick={() => onUnlock && onUnlock(user)} className={`text-[9px] font-black uppercase tracking-widest flex items-center gap-1 transition-colors ${isLight ? 'text-amber-500 hover:text-amber-600' : 'text-amber-300 hover:text-amber-200'}`}>
              Desbloquear <Lock size={12} />
            </button>
          )}
        </div>
        <button onClick={onToggleStatus} className={`w-9 h-4.5 rounded-full relative transition-all ${statusActive ? 'bg-emerald-600 group-hover:bg-emerald-500' : isLight ? 'bg-slate-300' : 'bg-slate-700'}`}>
          <motion.div animate={{ left: statusActive ? 22 : 3 }} className="absolute top-0.75 w-3 h-3 bg-white rounded-full shadow-lg" />
        </button>
      </div>
    </motion.div>
  );
});

const InputGroup = ({ label, placeholder, icon, type = "text", isLight }) => (
  <div className="space-y-1.5">
    <label className={`text-[9px] font-black uppercase tracking-widest ml-1 ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>{label}</label>
    <div className="relative group">
      {icon && <div className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${isLight ? 'text-slate-500 group-focus-within:text-indigo-600' : 'text-slate-600 group-focus-within:text-indigo-400'}`}>{icon}</div>}
      <input 
        type={type}
        placeholder={placeholder}
        className={`w-full border rounded-xl py-2.5 ${icon ? 'pl-9' : 'px-4'} pr-4 text-xs focus:outline-none focus:border-indigo-500 transition-all ${isLight ? 'bg-slate-50 border-slate-200 text-slate-800 placeholder:text-slate-400' : 'bg-white/5 border-white/10 text-slate-200 placeholder:text-slate-700'}`}
      />
    </div>
  </div>
);

const FilterSelect = ({ label, isLight }) => (
  <button className={`flex items-center gap-3 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest group border transition-all ${isLight ? 'bg-slate-100 border-slate-200 text-slate-600 hover:text-slate-900' : 'bg-white/5 border-white/5 text-slate-400 hover:text-white'}`}>
    {label} <ChevronDown size={12} className="opacity-30" />
  </button>
);

export default function AdminConsole({ apiBaseUrl }) {
  return (
    <div className="min-h-screen w-full">
      <App apiBaseUrl={apiBaseUrl} />
    </div>
  );
}
